import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  fetchRevenuePlanner,
  fetchRevenueGoalSummary,
  syncRevenueGoalFinance
} from 'api'
import { showToast } from 'app/helpers'

import GuideButton from './GuideButton'
import {
  defaultPlanningMonth,
  inr,
  monthLabel,
  monthOptions
} from './monthHelpers'
import { useFillHeight } from './useFillHeight'

// The All Centres goal board: for the picked month, every centre's target,
// what is ALREADY committed (preschool installments paid in advance + the
// manual override for collections Alfred can't see), and the actionable
// gap the centre team still has to collect. The afterschool wallet
// component ships with the chase list (P2) — until then the breakdown
// tooltip says so instead of showing a fake zero.
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  tableWrap: { maxHeight: '68vh' },
  empty: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  gapChip: { backgroundColor: '#EF5D34', color: '#fff', fontWeight: 600 },
  aheadChip: { backgroundColor: '#2E7D32', color: '#fff', fontWeight: 600 },
  bar: { width: 110, height: 8, borderRadius: 4 },
  percent: { fontSize: 12, minWidth: 38 },
  muted: { color: theme.palette.text.secondary },
  money: { whiteSpace: 'nowrap' },
  // Weighted pipeline is a FORECAST — coloured distinctly so it never reads
  // as committed cash.
  pipeline: { whiteSpace: 'nowrap', color: '#6A1B9A', fontWeight: 600 },
  chase: { whiteSpace: 'nowrap', color: '#EF5D34', fontWeight: 600 },
  coversChip: { backgroundColor: '#2E7D32', color: '#fff', fontWeight: 600 },
  tabs: { marginBottom: theme.spacing(1) },
  totalRow: { '& td': { fontWeight: 700 } },
  committedCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap'
  }
}))

const percentToGoal = (row) => {
  if (!row.target) return null
  return Math.min(
    100,
    Math.round((row.committed.total / row.target) * 100)
  )
}

const BreakdownTooltip = ({ row }) => (
  <Box>
    <Typography variant="caption" component="div">
      Preschool tuition: {inr(row.committed.preschool_tuition)}
    </Typography>
    <Typography variant="caption" component="div">
      AIM: {inr(row.committed.preschool_aim)}
    </Typography>
    <Typography variant="caption" component="div">
      Afterschool wallet:{' '}
      {row.committed.afterschool == null
        ? 'not computed yet'
        : inr(row.committed.afterschool)}
    </Typography>
    <Typography variant="caption" component="div">
      Manual override: {inr(row.committed.override)}
      {row.override_note ? ` — ${row.override_note}` : ''}
    </Typography>
  </Box>
)

// The planner grid credits the weighted enquiry pipeline against each
// centre's gap: chase-still-needed = max(0, gap − weighted pipeline). Pipeline
// is a forecast (purple), kept distinct from committed cash and never folded
// into the gap finance reconciles.
const PlannerGrid = ({ loading, rows, totals, classes, tableRef, tableMaxHeight }) => {
  if (loading) {
    return (
      <Box className={classes.empty}>
        <CircularProgress size={28} />
      </Box>
    )
  }
  const anyUnpriced = rows.some((r) => r.unpriced_count > 0)
  return (
    <>
      {anyUnpriced && (
        <Typography variant="caption" className={classes.muted} display="block" gutterBottom>
          Some enquiries couldn&apos;t be priced (fee not set up) — those count
          as ₹0, so pipeline coverage is understated where flagged.
        </Typography>
      )}
      <TableContainer
        component={Paper}
        ref={tableRef}
        style={{ maxHeight: tableMaxHeight }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Centre</TableCell>
              <TableCell align="right">
                <Tooltip title="The month's revenue goal, set on Set Targets">
                  <span>Target</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Money already secured — advance fees + wallet + finance adjustment">
                  <span>Committed</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Enquiry potential — WEIGHTED: each enquiry's fee × its conversion chance. A forecast, not cash.">
                  <span>Pipeline potential</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Best case — if every enquiry converted at 100%">
                  <span>Best case</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Gap left even after crediting the pipeline forecast — this must come from chasing existing parents">
                  <span>Chase still needed</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="How much of the gap the pipeline forecast covers">
                  <span>Coverage</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.centre_uuid} hover>
                <TableCell>
                  {row.centre_name}
                  {row.unpriced_count > 0 ? (
                    <Tooltip title={`${row.unpriced_count} enquiry program(s) unpriced`}>
                      <InfoOutlinedIcon fontSize="inherit" className={classes.muted} style={{ marginLeft: 4 }} />
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell align="right" className={classes.money}>
                  {row.target === null ? <span className={classes.muted}>—</span> : inr(row.target)}
                </TableCell>
                <TableCell align="right" className={classes.money}>
                  {inr(row.committed)}
                </TableCell>
                <TableCell align="right" className={classes.pipeline}>
                  {inr(row.weighted_pipeline)}
                </TableCell>
                <TableCell align="right" className={classes.muted}>
                  {inr(row.raw_pipeline)}
                </TableCell>
                <TableCell align="right">
                  {row.target === null ? (
                    <span className={classes.muted}>set a target</span>
                  ) : row.pipeline_covers_gap ? (
                    <Chip size="small" className={classes.coversChip} label="pipeline covers gap" />
                  ) : (
                    <span className={classes.chase}>{inr(row.chase_still_needed)}</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.pipeline_coverage_pct === null ? (
                    <span className={classes.muted}>—</span>
                  ) : (
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, row.pipeline_coverage_pct)}
                        className={classes.bar}
                      />
                      <span className={classes.percent}>{row.pipeline_coverage_pct}%</span>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length > 0 && (
              <TableRow className={classes.totalRow}>
                <TableCell>All centres</TableCell>
                <TableCell align="right">{inr(totals.target)}</TableCell>
                <TableCell align="right">{inr(totals.committed)}</TableCell>
                <TableCell align="right" className={classes.pipeline}>{inr(totals.weighted)}</TableCell>
                <TableCell align="right" className={classes.muted}>{inr(totals.raw)}</TableCell>
                <TableCell align="right" className={classes.chase}>{inr(totals.chase)}</TableCell>
                <TableCell />
              </TableRow>
            )}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className={classes.empty}>
                  No centres found for this month.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

const AllCentresBoard = () => {
  const classes = useStyles()
  const [month, setMonth] = useState(defaultPlanningMonth())
  const [view, setView] = useState('board') // 'board' | 'planner'
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [plannerRows, setPlannerRows] = useState([])
  const [plannerLoading, setPlannerLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [tableRef, tableMaxHeight] = useFillHeight()

  const load = useCallback(async (m) => {
    setLoading(true)
    try {
      const data = await fetchRevenueGoalSummary(m)
      setRows(data?.centres || [])
    } catch (error) {
      showToast('Could not load the goal board', 'error')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(month)
  }, [month, load])

  // The planner grid credits the weighted enquiry pipeline against the gap —
  // fetched lazily, only when the Planner tab is active.
  useEffect(() => {
    if (view !== 'planner') return
    let cancelled = false
    const loadPlanner = async () => {
      setPlannerLoading(true)
      try {
        const data = await fetchRevenuePlanner(month, 'monthly')
        if (!cancelled) setPlannerRows(data?.centres || [])
      } catch (error) {
        if (!cancelled) {
          showToast('Could not load the planner', 'error')
          setPlannerRows([])
        }
      } finally {
        if (!cancelled) setPlannerLoading(false)
      }
    }
    loadPlanner()
    return () => {
      cancelled = true
    }
  }, [view, month])

  // "Finance says" (PayFlow recon) renders only when the feed is live —
  // comparison column, deliberately not part of any gap math.
  const hasFinance = useMemo(
    () => rows.some((r) => r.finance_says && r.finance_says.total_revenue !== null),
    [rows]
  )

  const totals = useMemo(() => {
    const withTarget = rows.filter((r) => r.target !== null)
    return {
      target: withTarget.reduce((s, r) => s + (r.target || 0), 0),
      committed: rows.reduce((s, r) => s + (r.committed?.total || 0), 0),
      gap: withTarget.reduce((s, r) => s + (r.gap || 0), 0),
      finance: rows.reduce(
        (s, r) => s + (r.finance_says?.total_revenue || 0),
        0
      )
    }
  }, [rows])

  const plannerTotals = useMemo(() => {
    const withTarget = plannerRows.filter((r) => r.target !== null)
    return {
      target: withTarget.reduce((s, r) => s + (r.target || 0), 0),
      committed: plannerRows.reduce((s, r) => s + (r.committed || 0), 0),
      weighted: plannerRows.reduce((s, r) => s + (r.weighted_pipeline || 0), 0),
      raw: plannerRows.reduce((s, r) => s + (r.raw_pipeline || 0), 0),
      chase: withTarget.reduce((s, r) => s + (r.chase_still_needed || 0), 0)
    }
  }, [plannerRows])

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="h5">Revenue Goals — All Centres</Typography>
            <GuideButton title="How the All Centres board works">
              <Typography variant="body2" gutterBottom>
                One row per centre for the picked month (defaults to the
                coming month):
              </Typography>
              <ol style={{ paddingLeft: 18, lineHeight: 1.7 }}>
                <li>
                  <b>Target</b> — set by finance on the Set Targets page
                  (GST-inclusive: tuition + AIM + Active Care).
                </li>
                <li>
                  <b>Committed</b> — money already secured for the month:
                  preschool installments paid in advance (spread across the
                  months they cover) plus finance&apos;s manual adjustment for
                  collections outside Alfred. Hover the (i) for the split.
                </li>
                <li>
                  <b>Gap</b> — target minus committed: what the centre still
                  has to collect. Green means the centre is ahead.
                </li>
                <li>
                  <b>Finance says</b> (when shown) — PayFlow&apos;s revenue
                  number for the same month. The{' '}
                  <b>Use Finance as committed</b> button (top right) adopts
                  these as the real actuals: each centre&apos;s manual override
                  is set to the difference, so Committed lands exactly on the
                  Finance number. Re-run it any time finance&apos;s numbers
                  move; centres PayFlow doesn&apos;t report are left untouched.
                </li>
              </ol>
              <Typography variant="body2">
                This board and Set Targets live under <b>HQ</b>; the
                centre&apos;s working view — including who to call — is the
                centre-facing <b>Revenue Goals → My Centre</b>.
              </Typography>
            </GuideButton>
          </Box>
          <Typography variant="body2" className={classes.muted}>
            {monthLabel(month)}
          </Typography>
        </Box>
        <Box display="flex" gridGap={12} alignItems="center">
          {hasFinance && (
            <Tooltip title="Make every centre's Committed equal PayFlow's Finance-says number (finance = the real actuals)">
              <span>
            <Button
              size="small"
              variant="outlined"
              style={{ textTransform: 'none' }}
              disabled={syncing}
              onClick={async () => {
                // Overwrites every centre's manual override for the month —
                // deliberate, but worth one explicit confirm.
                // eslint-disable-next-line no-alert
                if (
                  !window.confirm(
                    `Set every centre's committed for ${monthLabel(month)} to ` +
                      "PayFlow's Finance-says number? This replaces the manual " +
                      'overrides with the reconciliation delta.'
                  )
                )
                  return
                setSyncing(true)
                try {
                  const result = await syncRevenueGoalFinance(month)
                  const skipped = result?.skipped?.length
                  showToast(
                    `Committed synced to Finance for ${result?.updated?.length || 0} centres` +
                      (skipped ? ` (${skipped} skipped — no PayFlow number)` : '')
                  )
                  load(month)
                } catch (error) {
                  showToast(
                    error?.response?.data?.detail || 'Could not sync from PayFlow',
                    'error'
                  )
                } finally {
                  setSyncing(false)
                }
              }}
            >
              {syncing ? 'Syncing…' : 'Use Finance as committed'}
            </Button>
              </span>
            </Tooltip>
          )}
          <FormControl variant="outlined" size="small">
            <InputLabel>Month</InputLabel>
            <Select
              label="Month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {monthOptions(6).map((m) => (
                <MenuItem key={m} value={m}>
                  {monthLabel(m)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Tabs
        className={classes.tabs}
        value={view}
        onChange={(e, v) => setView(v)}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab
          style={{ textTransform: 'none' }}
          value="board"
          label={
            <Tooltip title="Target vs money already secured vs gap, per centre">
              <span>Board</span>
            </Tooltip>
          }
        />
        <Tab
          style={{ textTransform: 'none' }}
          value="planner"
          label={
            <Tooltip title="Adds the enquiry pipeline: how much of each gap new enquiries are likely to cover">
              <span>Planner</span>
            </Tooltip>
          }
        />
      </Tabs>

      {view === 'planner' ? (
        <PlannerGrid
          loading={plannerLoading}
          rows={plannerRows}
          totals={plannerTotals}
          classes={classes}
          tableRef={tableRef}
          tableMaxHeight={tableMaxHeight}
        />
      ) : loading ? (
        <Box className={classes.empty}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          ref={tableRef}
          style={{ maxHeight: tableMaxHeight }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Centre</TableCell>
                <TableCell align="right">
                  <Tooltip title="The month's revenue goal, set on Set Targets">
                    <span>Target</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Money already secured — advance fees + wallet + finance adjustment (hover the ⓘ for the split)">
                    <span>Committed</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Target minus committed — what the centre still has to collect">
                    <span>Gap</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Committed as a share of the target">
                    <span>% to goal</span>
                  </Tooltip>
                </TableCell>
                {hasFinance && (
                  <TableCell align="right">
                    <Tooltip title="PayFlow's revenue number for the same month — the finance actuals">
                      <span>Finance says</span>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const pct = percentToGoal(row)
                return (
                  <TableRow key={row.centre_uuid} hover>
                    <TableCell>{row.centre_name}</TableCell>
                    <TableCell align="right" className={classes.money}>
                      {row.target === null ? (
                        <span className={classes.muted}>—</span>
                      ) : (
                        inr(row.target)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        className={classes.committedCell}
                        justifyContent="flex-end"
                      >
                        <span className={classes.money}>
                          {inr(row.committed.total)}
                        </span>
                        <Tooltip title={<BreakdownTooltip row={row} />}>
                          <InfoOutlinedIcon
                            fontSize="inherit"
                            className={classes.muted}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {row.target === null ? (
                        <span className={classes.muted}>set a target</span>
                      ) : row.surplus > 0 ? (
                        <Chip
                          size="small"
                          className={classes.aheadChip}
                          label={`ahead by ${inr(row.surplus)}`}
                        />
                      ) : (
                        <Chip
                          size="small"
                          className={classes.gapChip}
                          label={inr(row.gap)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {pct === null ? (
                        <span className={classes.muted}>—</span>
                      ) : (
                        <Box display="flex" alignItems="center" gridGap={8}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            className={classes.bar}
                          />
                          <span className={classes.percent}>{pct}%</span>
                        </Box>
                      )}
                    </TableCell>
                    {hasFinance && (
                      <TableCell align="right">
                        {row.finance_says &&
                        row.finance_says.total_revenue !== null ? (
                          <Tooltip
                            title={`PayFlow ${
                              row.finance_says.estimated ? '(provisional) ' : ''
                            }— preschool ${inr(
                              row.finance_says.preschool
                            )}, afterschool ${inr(row.finance_says.afterschool)}`}
                          >
                            <span className={classes.money}>
                              {inr(row.finance_says.total_revenue)}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className={classes.muted}>—</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
              {rows.length > 0 && (
                <TableRow className={classes.totalRow}>
                  <TableCell>All centres</TableCell>
                  <TableCell align="right">{inr(totals.target)}</TableCell>
                  <TableCell align="right">{inr(totals.committed)}</TableCell>
                  <TableCell align="right">{inr(totals.gap)}</TableCell>
                  <TableCell />
                  {hasFinance && (
                    <TableCell align="right" className={classes.money}>
                      {inr(totals.finance)}
                    </TableCell>
                  )}
                </TableRow>
              )}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={hasFinance ? 6 : 5} className={classes.empty}>
                    No centres found for this month.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default AllCentresBoard
